import React, { useEffect } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { pickDirtyNested } from '@/utils/patchFromDirty';
import {
	Input,
	InputError,
	InputLabel,
	InputWrapper,
} from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import {
	MapPin,
	Building2,
	Phone,
	Hash,
	IdCard,
	Save,
	CircleChevronLeft,
} from 'lucide-react';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { ToggleInput } from '@/components/ui/ToggleInput';
import { maskCNPJ, maskPhone, maskPostalCode, removeMask } from '@/utils/masks';

export const clinicSchema = z.object({
	clinic: z.object({
		name: z
			.string()
			.min(1, 'Nome é obrigatório')
			.min(3, 'Nome deve ter no mínimo 3 caracteres')
			.max(100, 'Nome deve ter no máximo 100 caracteres')
			.regex(
				/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/,
				'Apenas letras e acentos são permitidos',
			),

		cnpj: z
			.string()
			.min(1, 'CNPJ é obrigatório')
			.transform(removeMask)
			.refine(
				(val) => val.length === 14,
				'CNPJ deve conter exatamente 14 números',
			)
			.refine((val) => /^\d{14}$/.test(val)),

		phone_number: z
			.string()
			.min(1, 'Telefone é obrigatório')
			.transform(removeMask)
			.refine(
				(val) => val.length === 11,
				'O tefefone deve conter exatamente 11 números',
			)
			.refine((val) => /^\d{11}$/.test(val)),

		active: z.boolean(),
	}),
	address: z.object({
		street_name: z
			.string({
				required_error: 'Rua é obrigatório',
				invalid_type_error: 'Rua é obrigatório',
			})
			.min(1, 'Endereço é obrigatório')
			.min(5, 'Endereço deve ter no mínimo 5 caracteres')
			.max(255, 'Endereço deve ter no máximo 255 caracteres')
			.regex(/^[0-9A-Za-zÀ-ÖØ-öø-ÿ\s]+$/, 'Caracteres inválidos'),

		district_name: z
			.string({
				required_error: 'Bairro é obrigatório',
				invalid_type_error: 'Bairro é obrigatório',
			})
			.min(1, 'Bairro é obrigatório')
			.min(3, 'Bairro deve ter no mínimo 3 caracteres')
			.max(100, 'Bairro deve ter no máximo 100 caracteres')
			.regex(/^[0-9A-Za-zÀ-ÖØ-öø-ÿ\s]+$/, 'Caracteres inválidos'),

		postal_code: z
			.string({
				required_error: 'CEP é obrigatório',
				invalid_type_error: 'CEP é obrigatório',
			})
			.min(1, 'CEP é obrigatório')
			.transform(removeMask)
			.refine(
				(val) => val.length === 8,
				'CEP deve conter exatamente 8 números',
			)
			.refine((val) => /^\d{8}$/.test(val)),

		number: z
			.string({
				required_error: 'Número é obrigatório',
				invalid_type_error: 'Número é obrigatório',
			})
			.min(1, 'Número é obrigatório')
			.min(1, 'Número deve ter no mínimo 1 caractere')
			.regex(
				/^(?=.*\d)[0-9A-Za-z]*$/,
				'Número deve conter pelo menos 1 dígito',
			),

		city: z
			.string({
				required_error: 'Cidade é obrigatório',
				invalid_type_error: 'Cidade é obrigatório',
			})
			.min(1, 'Cidade é obrigatória')
			.min(3, 'Cidade deve ter no mínimo 3 caracteres')
			.max(255, 'Cidade deve ter no máximo 255 caracteres')
			.regex(/^[0-9A-Za-zÀ-ÖØ-öø-ÿ\s]+$/, 'Caracteres inválidos'),

		state: z.enum(
			[
				'AC',
				'AL',
				'AP',
				'AM',
				'BA',
				'CE',
				'DF',
				'ES',
				'GO',
				'MA',
				'MT',
				'MS',
				'MG',
				'PA',
				'PB',
				'PR',
				'PE',
				'PI',
				'RJ',
				'RN',
				'RS',
				'RO',
				'RR',
				'SC',
				'SP',
				'SE',
				'TO',
			],
			{
				errorMap: () => ({ message: 'Estado inválido' }),
			},
		),

		ref: z
			.string()
			.max(255, 'Referência deve ter no máximo 255 caracteres')
			.regex(/^[0-9A-Za-zÀ-ÖØ-öø-ÿ\s]*$/, 'Caracteres inválidos')
			.nullable()
			.transform((val) => (val ? val : null)),
	}),
});

interface ClinicFormProps {
	onSubmit: (data: Clinics.Create | Partial<Clinics.Create>) => void;
	onBack: () => void;
	defaultValues?: Clinics.ClinicDetail;
	isSubmitting?: boolean;
	formTitle?: string;
	submitButtonText?: string;
}

const ClinicForm: React.FC<ClinicFormProps> = ({
	onSubmit,
	defaultValues,
	isSubmitting: externalIsSubmitting = false,
	formTitle,
	submitButtonText,
	onBack,
}) => {
	const isEditMode = !!defaultValues?.clinic?.id;

	const {
		register,
		handleSubmit,
		formState: {
			errors,
			isSubmitting: internalIsSubmitting,
			dirtyFields,
			isDirty,
		},
		watch,
		setValue,
		control,
		reset,
	} = useForm<Clinics.Create>({
		resolver: zodResolver(clinicSchema),
		defaultValues: {
			...defaultValues,
		},
	});

	useEffect(() => {
		if (!defaultValues) return;
		reset({
			clinic: {
				name: defaultValues.clinic.name,
				cnpj: defaultValues.clinic.cnpj,
				phone_number: defaultValues.clinic.phone_number,
				active: defaultValues.clinic.active,
			},
			address: {
				street_name: defaultValues.address.street_name,
				district_name: defaultValues.address.district_name,
				postal_code: defaultValues.address.postal_code,
				number: defaultValues.address.number,
				city: defaultValues.address.city,
				state: defaultValues.address.state,
				ref: defaultValues.address.ref,
			},
		});
	}, [defaultValues?.clinic?.id, reset]);

	const isSubmitting = externalIsSubmitting || internalIsSubmitting;
	const stateWatch = watch('address.state');

	const isFieldError = (error: any): error is FieldError => {
		return error && typeof error === 'object' && 'message' in error;
	};

	const submitClinic = (data: Clinics.Create) => {
		if (!isEditMode) {
			onSubmit(data);
			return;
		}
		const patch = pickDirtyNested(
			data as unknown as Record<string, unknown>,
			dirtyFields as object,
		) as Partial<Clinics.Create> | undefined;
		if (patch && Object.keys(patch).length > 0) {
			onSubmit(patch);
		}
	};

	return (
		<form
			onSubmit={handleSubmit(submitClinic)}
			className="max-w-2xl space-y-6 bg-inherit"
		>
			<div className="flex w-full flex-wrap items-center justify-between">
				<h2 className="text-lg font-bold text-dark">{formTitle}</h2>
				<ToggleInput
					control={control}
					defaultValue={defaultValues?.clinic.active ?? true}
					name="clinic.active"
					label="Ativa"
				/>
			</div>

			<div className="space-y-4">
				<h3 className="text-md flex items-center gap-2 font-semibold text-dark">
					Informações da Clínica
				</h3>

				<div className="space-y-1">
					<InputLabel
						text="Nome da Clínica"
						icon={<Building2 className="h-4 w-4 text-dark" />}
					/>
					<InputWrapper>
						<Input
							placeholder="Ex: Clínica Saúde Total"
							{...register('clinic.name')}
						/>
					</InputWrapper>
					{errors.clinic?.name?.message && (
						<InputError error={errors.clinic?.name?.message} />
					)}
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div className="space-y-1">
						<InputLabel
							text="CNPJ"
							icon={<IdCard className="h-4 w-4 text-dark" />}
						/>
						<InputWrapper>
							<Input
								value={maskCNPJ(watch('clinic.cnpj') || '')}
								{...register('clinic.cnpj')}
								placeholder="Ex: 12.345.678/0001-99"
							/>
						</InputWrapper>
						{errors?.clinic?.cnpj?.message && (
							<InputError error={errors.clinic?.cnpj?.message} />
						)}
					</div>

					<div className="space-y-1">
						<InputLabel
							text="Telefone"
							icon={<Phone className="h-4 w-4 text-dark" />}
						/>
						<InputWrapper>
							<Input
								value={maskPhone(
									watch('clinic.phone_number') || '',
								)}
								{...register('clinic.phone_number')}
								placeholder="Ex: (11) 98765-4321"
								maxLength={15}
							/>
						</InputWrapper>
						{errors.clinic?.phone_number?.message && (
							<InputError
								error={errors.clinic?.phone_number.message}
							/>
						)}
					</div>
				</div>
			</div>

			<div className="space-y-4">
				<h3 className="text-md flex items-center gap-2 font-semibold text-dark">
					Endereço da Clínica
				</h3>

				<div className="space-y-1">
					<InputLabel
						text="Rua"
						icon={<MapPin className="h-4 w-4 text-dark" />}
					/>
					<InputWrapper>
						<Input
							placeholder="Ex: Rua das Flores"
							{...register('address.street_name')}
						/>
					</InputWrapper>
					{errors?.address?.street_name?.message && (
						<InputError
							error={errors.address.street_name.message}
						/>
					)}
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div className="space-y-1">
						<InputLabel
							text="Número"
							icon={<Hash className="h-4 w-4 text-dark" />}
						/>
						<InputWrapper>
							<Input
								placeholder="Ex: 123A"
								{...register('address.number')}
							/>
						</InputWrapper>
						{errors?.address?.number?.message && (
							<InputError error={errors.address.number.message} />
						)}
					</div>

					<div className="space-y-1">
						<InputLabel
							text="Complemento (Opcional)"
							icon={<MapPin className="h-4 w-4 text-dark" />}
						/>
						<InputWrapper>
							<Input
								placeholder="Ex: Próximo ao mercado"
								{...register('address.ref')}
							/>
						</InputWrapper>
						{errors?.address?.ref &&
							isFieldError(errors.address.ref) && (
								<InputError
									error={errors.address.ref.message || ''}
								/>
							)}
					</div>
				</div>

				<div className="space-y-1">
					<InputLabel
						text="Bairro"
						icon={<MapPin className="h-4 w-4 text-dark" />}
					/>
					<InputWrapper>
						<Input
							placeholder="Ex: Centro"
							{...register('address.district_name')}
						/>
					</InputWrapper>
					{errors?.address?.district_name?.message && (
						<InputError
							error={errors.address.district_name.message}
						/>
					)}
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
					<div className="space-y-1">
						<InputLabel
							text="CEP"
							icon={<MapPin className="h-4 w-4 text-dark" />}
						/>
						<InputWrapper>
							<Input
								value={maskPostalCode(
									watch('address.postal_code') || '',
								)}
								{...register('address.postal_code')}
								placeholder="Ex: 12345-678"
							/>
						</InputWrapper>
						{errors.address?.postal_code?.message && (
							<InputError
								error={errors.address.postal_code.message}
							/>
						)}
					</div>

					<div className="space-y-1">
						<InputLabel
							text="Cidade"
							icon={<MapPin className="h-4 w-4 text-dark" />}
						/>
						<InputWrapper>
							<Input
								placeholder="Ex: São Paulo"
								{...register('address.city')}
							/>
						</InputWrapper>
						{errors.address?.city?.message && (
							<InputError error={errors.address.city.message} />
						)}
					</div>

					<div className="space-y-1">
						<InputLabel
							text="Estado (UF)"
							icon={<MapPin className="h-4 w-4 text-dark" />}
						/>
						<Select
							value={stateWatch}
							onValueChange={(value) =>
								setValue(
									'address.state',
									value as Clinics.BrazilianState,
									{
										shouldDirty: true,
									},
								)
							}
						>
							<SelectTrigger className="h-fit w-full">
								<SelectValue>
									{stateWatch || 'Selecione um estado'}
								</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									{[
										'AC',
										'AL',
										'AP',
										'AM',
										'BA',
										'CE',
										'DF',
										'ES',
										'GO',
										'MA',
										'MT',
										'MS',
										'MG',
										'PA',
										'PB',
										'PR',
										'PE',
										'PI',
										'RJ',
										'RN',
										'RS',
										'RO',
										'RR',
										'SC',
										'SP',
										'SE',
										'TO',
									].map((state) => (
										<SelectItem key={state} value={state}>
											{state}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
						{errors.address?.state?.message && (
							<InputError error={errors.address.state.message} />
						)}
					</div>
				</div>
			</div>

			<div className="flex justify-between">
				<Button
					onClick={onBack}
					text="voltar"
					type="button"
					variant="outline"
					startIcon={<CircleChevronLeft className="w-4 text-dark" />}
				/>

				<Button
					type="submit"
					disabled={isSubmitting || (isEditMode && !isDirty)}
					endIcon={<Save className="w-4 text-light" />}
					text={submitButtonText || 'Salvar'}
				/>
			</div>
		</form>
	);
};

export { ClinicForm };
